import { type Item, TAGS_MAX } from '@hugo/core'
import { HouseIcon, XIcon } from 'lucide-react'
import { useId, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { useRunTool, useSettings, useTags } from '@/lib/items'
import { parseTag, tagLabel } from '@/lib/tags'

/**
 * An item's tags in the details: chips with a remove button, and a field to add one (Enter or a
 * comma; the tags in use are offered). Personal tags carry a house. Each change is one undoable
 * edit.
 */
export function TagsField({ item, id }: { item: Item; id: string }) {
  const { run } = useRunTool()
  const tags = useTags()
  const personalTags = useSettings().data?.personalTags ?? []
  const listId = useId()
  const [text, setText] = useState('')
  const [error, setError] = useState<string | null>(null)
  const offered = (tags.data ?? [])
    .map((tag) => tag.name)
    .filter((name) => !item.tags.includes(name))

  const save = (next: string[], done: string) =>
    run('item.update', { id: item.id, tags: next }, { done, failed: 'Could not change the tags' })

  const add = (raw: string) => {
    if (!raw.trim()) return
    const tag = parseTag(raw)
    if (!tag) {
      setError('A tag is one word: letters, digits, - and _')
      return
    }
    setText('')
    setError(null)
    if (item.tags.includes(tag)) return
    if (item.tags.length >= TAGS_MAX) {
      setError(`At most ${TAGS_MAX} tags`)
      return
    }
    void save([...item.tags, tag], `Tagged ${tagLabel(tag)}`)
  }

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-1" data-testid="item-tags">
      <div className="flex min-w-0 flex-wrap items-center gap-1">
        {item.tags.map((tag) => {
          const personal = personalTags.includes(tag)
          return (
            <Badge
              key={tag}
              variant="outline"
              className="h-6 gap-1 pr-0.5 font-normal"
              data-testid="item-tag"
            >
              {personal ? <HouseIcon aria-label="Personal" className="size-3" /> : null}
              {tagLabel(tag)}
              <button
                type="button"
                aria-label={`Remove ${tagLabel(tag)}`}
                className="inline-flex size-5 items-center justify-center rounded-full text-muted-foreground outline-none hover:bg-muted hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50"
                onClick={() =>
                  void save(
                    item.tags.filter((name) => name !== tag),
                    `Removed ${tagLabel(tag)}`,
                  )
                }
              >
                <XIcon aria-hidden="true" className="size-3" />
              </button>
            </Badge>
          )
        })}
        <Input
          id={id}
          list={listId}
          value={text}
          placeholder={item.tags.length === 0 ? 'Add a tag, e.g. family' : 'Add a tag'}
          aria-invalid={error !== null || undefined}
          aria-describedby={error ? `${id}-error` : undefined}
          className="h-7 w-36 min-w-0 flex-1"
          onChange={(event) => {
            const value = event.target.value
            // A comma or a space ends a tag, like Enter.
            if (/[,\s]$/.test(value) && value.trim()) {
              add(value.slice(0, -1))
              return
            }
            setText(value)
            if (error) setError(null)
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              add(text)
            } else if (event.key === 'Backspace' && !text && item.tags.length > 0) {
              const last = item.tags.at(-1) as string
              void save(item.tags.slice(0, -1), `Removed ${tagLabel(last)}`)
            }
          }}
          onBlur={() => add(text)}
        />
        <datalist id={listId}>
          {offered.map((name) => (
            <option key={name} value={name} />
          ))}
        </datalist>
      </div>
      {error ? (
        <p id={`${id}-error`} className="text-xs text-destructive" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  )
}
